<template>
  <div>
    <app-layout>
      <template v-slot:header>
        <Header :title="$t('u_pass_student_title')" :isNewButton="false" />
      </template>
      <template v-slot:header-mobile>
        <HeaderMobile :title="$t('u_pass_student_title')" :isNewButton="false" />
      </template>

      <div class="container-fluid py-3">
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p class="mt-2 text-muted">{{ $t('loading') }}</p>
        </div>
        <div v-else>
            <template v-if="!checkStudentType">
                <warning-page
                    :title="$t('information')"
                    :message="$t('upass_student_request_information_message')"
                />
            </template>
             <template v-else>
                 <b-row class="mb-4">
                     <b-col cols="12">
                         <b-alert v-if="!isEligible" show variant="danger" class="d-flex align-items-center shadow-sm">
                             <i class="ri-error-warning-line ri-2x mr-3"></i>
                             <div>
                                 <h5 class="alert-heading mb-1">{{ $t('u_pass_not_eligible_title') }}</h5>
                                 <p class="mb-0">
                                     {{ displayMessage || $t('u_pass_registration_required_msg') }}
                                 </p>
                             </div>
                         </b-alert>

                         <b-alert v-else-if="hasPurchased" show variant="success" class="d-flex align-items-center shadow-sm">
                             <i class="ri-checkbox-circle-line ri-2x mr-3"></i>
                             <div>
                                 <h5 class="alert-heading mb-1">{{ $t('u_pass_purchased_title') }}</h5>
                                 <p class="mb-0">
                                     {{ $t('u_pass_purchased_msg') }}
                                 </p>
                             </div>
                         </b-alert>

                         <b-alert v-else-if="!activePeriod" show variant="warning" class="d-flex align-items-center shadow-sm">
                             <i class="ri-calendar-line ri-2x mr-3"></i>
                             <div>
                                 <h5 class="alert-heading mb-1">{{ $t('u_pass_window_closed_title') }}</h5>
                                 <p class="mb-0">
                                     {{ displayMessage || $t('u_pass_window_closed_msg') }}
                                 </p>
                             </div>
                         </b-alert>

                         <b-alert v-else-if="isSoldOut" show variant="warning" class="d-flex align-items-center shadow-sm">
                             <i class="ri-alarm-warning-line ri-2x mr-3"></i>
                             <div>
                                 <h5 class="alert-heading mb-1">{{ $t('u_pass_sold_out_title') }}</h5>
                                 <p class="mb-0">
                                     {{ $t('u_pass_sold_out_msg') }}
                                 </p>
                             </div>
                         </b-alert>

                         <b-alert v-else-if="!isWindowOpen" show variant="info" class="d-flex align-items-center shadow-sm">
                             <i class="ri-calendar-line ri-2x mr-3"></i>
                             <div>
                                 <h5 class="alert-heading mb-1">{{ $t('u_pass_window_closed_title') }}</h5>
                                 <p class="mb-0">
                                     {{ $t('u_pass_window_closed_msg') }}
                                     <span v-if="activePeriod && activePeriod.start_date && activePeriod.end_date">
                          ({{ formatDateUS(activePeriod.start_date) }} - {{ formatDateUS(activePeriod.end_date) }})
                        </span>
                                 </p>
                             </div>
                         </b-alert>

                         <b-alert v-else show variant="primary" class="d-flex align-items-center shadow-sm">
                             <i class="ri-checkbox-circle-line ri-2x mr-3"></i>
                             <div>
                                 <h5 class="alert-heading mb-1">{{ $t('u_pass_available_title') }}</h5>
                                 <p class="mb-0">
                                     {{ $t('u_pass_available_msg') }}
                                 </p>
                             </div>
                         </b-alert>
                     </b-col>
                 </b-row>
                 <b-row>
                     <b-col md="6" class="mb-4">
                         <b-card header-tag="header" class="shadow-sm h-100">
                             <template v-slot:header>
                                 <div class="d-flex align-items-center justify-content-between">
                                     <h5 class="mb-0 text-primary"><i class="ri-information-line mr-2"></i>{{ $t('u_pass_period_info') }}</h5>
                                     <b-badge :variant="activePeriod && isWindowOpen ? 'success' : 'secondary'">
                                         {{ activePeriod && isWindowOpen ? $t('open') : $t('closed') }}
                                     </b-badge>
                                 </div>
                             </template>

                             <b-list-group flush v-if="activePeriod">
                                 <b-list-group-item class="d-flex justify-content-between align-items-center">
                                     <span><i class="ri-money-dollar-circle-line mr-2 text-muted"></i>{{ $t('u_pass_price') }}:</span>
                                     <strong class="h5 mb-0 text-success">{{ formatToCurrency(activePeriod.price) }}</strong>
                                 </b-list-group-item>

                                 <b-list-group-item class="d-flex justify-content-between align-items-center">
                                     <span><i class="ri-calendar-event-line mr-2 text-muted"></i>{{ $t('start_date') }}:</span>
                                     <strong>{{ formatDateUS(activePeriod.start_date) }}</strong>
                                 </b-list-group-item>

                                 <b-list-group-item class="d-flex justify-content-between align-items-center">
                                     <span><i class="ri-calendar-check-line mr-2 text-muted"></i>{{ $t('end_date') }}:</span>
                                     <strong>{{ formatDateUS(activePeriod.end_date) }}</strong>
                                 </b-list-group-item>

                                 <b-list-group-item class="d-flex justify-content-between align-items-center">
                                     <span><i class="ri-stack-line mr-2 text-muted"></i>{{ $t('u_pass_inventory_remaining') }}:</span>
                                     <b-badge :variant="remainingInventory > 0 ? 'info' : 'danger'" class="p-2">
                                         {{ remainingInventory }} / {{ totalInventory }} {{ $t('passes_remaining') }}
                                     </b-badge>
                                 </b-list-group-item>
                             </b-list-group>

                             <div v-else class="text-center py-4 text-muted">
                                 <i class="ri-calendar-event-line ri-2x mb-2 d-block"></i>
                                 <span>{{ $t('u_pass_window_closed_msg') }}</span>
                             </div>

                             <div class="mt-3 p-2 bg-light rounded text-muted small">
                                 <i class="ri-user-star-line mr-1"></i>
                                 <strong>Mentora Students:</strong> Returning & current students purchase during the standard window. New Mentora students starting after September 4 can purchase during their FIRST Module registration period.
                             </div>
                         </b-card>
                     </b-col>

                     <b-col md="6" class="mb-4">
                         <b-card header-tag="header" class="shadow-sm h-100">
                             <template v-slot:header>
                                 <h5 class="mb-0 text-primary"><i class="ri-shopping-cart-line mr-2"></i>{{ $t('u_pass_request_status') }}</h5>
                             </template>

                             <div v-if="hasPurchased" class="text-center py-4">
                                 <div class="mb-3">
                                     <i class="ri-checkbox-circle-fill text-success" style="font-size: 3.5rem;"></i>
                                 </div>
                                 <h4 class="text-success mb-2">{{ $t('purchased') }}</h4>
                                 <p class="text-muted">
                                     {{ $t('u_pass_purchased_on') }}: <strong>{{ formatPurchaseDate(purchaseData.purchased_at) }}</strong>
                                 </p>
                                 <p v-if="purchaseData.payment_uuid" class="small text-monospace bg-light p-2 rounded">
                                     Ref ID: {{ purchaseData.payment_uuid }}
                                 </p>
                             </div>

                             <div v-else class="d-flex flex-column justify-content-between h-100 py-3">
                                 <div>
                                     <p class="text-muted">
                                         {{ $t('u_pass_purchase_instructions') }}
                                     </p>
                                 </div>
                                 <div class="mt-4">
                                     <b-button
                                         block
                                         size="lg"
                                         variant="success"
                                         :disabled="!canPurchase || startingPurchase"
                                         @click="startPurchase"
                                     >
                                         <b-spinner v-if="startingPurchase" small class="mr-2"></b-spinner>
                                         <i v-else class="ri-bank-card-line mr-2"></i>
                                         {{ $t('u_pass_buy_now_btn') }} <span v-if="activePeriod && activePeriod.price">({{ formatToCurrency(activePeriod.price) }})</span>
                                     </b-button>
                                 </div>
                             </div>
                         </b-card>
                     </b-col>
                 </b-row>
             </template>
        </div>
      </div>
    </app-layout>
  </div>
</template>

<script>
import AppLayout from "@/layouts/AppLayout";
import Header from "@/layouts/AppLayout/Header";
import HeaderMobile from "@/layouts/AppLayout/HeaderMobile";
import UPassService from "@/services/UPassService";
import CustomerService from "@/services/CustomerService";
import Base from "@/plugins/Base";
import moment from "moment";
import WarningPage from "@/components/page/WarningPage.vue";

export default {
  name: "StudentUPassRequest",
  components: {
      WarningPage,
    AppLayout,
    Header,
    HeaderMobile
  },
  metaInfo() {
    return {
      title: this.$t("u_pass_student_title")
    };
  },
  data() {
    return {
      loading: true,
      startingPurchase: false,
      statusData: {
        eligible: false,
        can_purchase: false,
        message: "",
        period: null,
        inventory: null,
        purchase: null
      }
    };
  },
  computed: {
    checkStudentType(){
        const user = this.$store.getters["auth/getUser"];
        const studentType =user?.active_student_program?.student_type
        return this.activePeriod.student_types.includes(studentType)
    } ,
    isEligible() {
      return !!this.statusData.eligible;
    },
    displayMessage() {
      const msg = this.statusData.message;
      if (!msg) return "";
      if (this.$te && this.$te(`api.${msg}`)) {
        return this.$t(`api.${msg}`);
      }
      return msg;
    },
    activePeriod() {
      return this.statusData.period || null;
    },
    inventory() {
      return this.statusData.inventory || null;
    },
    totalInventory() {
      return this.inventory ? Number(this.inventory?.academic_year?.total || 0) : 0;
    },
    remainingInventory() {
      return this.inventory ? Number(this.inventory?.academic_year?.available || 0) : 0;
    },
    purchaseData() {
      return this.statusData.purchase || null;
    },
    hasPurchased() {
      return !!this.purchaseData && (this.purchaseData.status === 'completed' || this.purchaseData.status === 'paid');
    },
    isWindowOpen() {
      return !!(this.activePeriod && this.activePeriod.is_active);
    },
    isSoldOut() {
      return this.totalInventory > 0 && this.remainingInventory <= 0;
    },
    canPurchase() {
      const hasPermission = this.checkPermission("upass_purchase");
      return !!this.statusData.can_purchase && !this.hasPurchased && hasPermission;
    },
    studentProgramId() {
      let user = this.$store.getters["auth/getUser"];
      return user?.active_student_program?.id;
    }
  },
  created() {
    this.fetchStudentStatus();
  },
  methods: {
    async fetchStudentStatus() {
      this.loading = true;
      try {
        const response = await UPassService.getStudentStatus();
        this.statusData = response.data?.data || response.data || {};
      } catch (error) {
        this.statusData = {
          eligible: false,
          can_purchase: false,
          message: error.data?.message || error.message || "",
          period: null,
          inventory: null,
          purchase: null
        };
      } finally {
        this.loading = false;
      }
    },
    async startPurchase() {
      if (!this.canPurchase) return;

      try {
        this.startingPurchase = true;
        await UPassService.purchaseStudentUPass();
        const response = await CustomerService.createEpaymentLogin({ student_program_id: this.studentProgramId });
        let data = response.data.data;
        Base.LocalStorage.set('epayment_token', data.token);
        this.$store.commit('epayment/setEpaymentToken', 'Bearer ' + data.token);
        Base.LocalStorage.set('epayment_user', data.customer);
        this.$store.commit('epayment/setEpaymentUser', data.customer);
        this.$router.push('/epayment');
      } catch (error) {
        this.showErrors(error, null);
      } finally {
        this.startingPurchase = false;
      }
    },
    formatPurchaseDate(dateStr) {
      if (!dateStr) return "";
      return moment(dateStr).format("MM/DD/YYYY HH:mm");
    },
    formatDateUS(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return !isNaN(d.getTime()) ? d.toLocaleDateString("en-US") : dateStr;
    }
  }
};
</script>
